import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { ArrowRight, Play, CheckCircle, Zap, Star, Shield, TrendingUp, Sparkles } from 'lucide-react';
import { mockData } from './mock';
import { useScrollAnimation, createScrollAnimationClasses } from '../hooks/useScrollAnimation';

export const HeroSection = () => {
  const [contentRef, contentVisible] = useScrollAnimation({ delay: 0 });
  const [imageRef, imageVisible] = useScrollAnimation({ delay: 200 });
  const [statsRef, statsVisible] = useScrollAnimation({ delay: 400 });
  const [buttonsRef, buttonsVisible] = useScrollAnimation({ delay: 600 });
  
  // Set to true when ready to show hero video again
  const USE_HERO_VIDEO = false;

  // Video state management
  const [videoError, setVideoError] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoRef = useRef(null);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
      {/* Enhanced Background Decorations */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-96 h-96 bg-blue-200/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-purple-200/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-green-200/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/4 right-1/4 w-48 h-48 bg-orange-200/10 rounded-full blur-3xl"></div>
      </div>

      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="container mx-auto px-2 sm:px-4 pt-20 pb-8">
        <div className="grid lg:grid-cols-2 gap-4 sm:gap-8 lg:gap-16 items-start pt-6">
          {/* Content */}
          <div ref={contentRef} className={`text-center lg:text-left ${createScrollAnimationClasses('fadeInLeft', contentVisible)}`}>
            {/* <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-100 to-indigo-100 px-6 py-3 rounded-full mb-6 border border-blue-200/50">
              <Sparkles size={10} className="text-blue-600" />
              <span className="text-blue-700 text-sm font-semibold">Leading Traffic Technology Provider in Oman</span>
            </div> */}
            
            <h1 className="text-2xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 lg:mb-8 leading-tight">
              Connecting Cities Through{' '}
              <span 
                className="bg-clip-text text-transparent"
                style={{ 
                  backgroundImage: 'linear-gradient(to right, rgb(6 47 136), rgb(147 51 234))' 
                }}
              >
                Leading Edge
              </span>{' '}
              Technology
            </h1>
            
            <p className="text-sm sm:text-base lg:text-xl text-gray-600 mb-6 sm:mb-8 lg:mb-10 leading-relaxed max-w-xl sm:max-w-2xl mx-auto lg:mx-0">
              Harness the power of Artificial Intelligence and Internet of Things to create 
              smarter, safer, and more connected urban environments with our innovative solutions.
            </p>

            {/* Key Benefits */}
            <div ref={statsRef} className={`grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 lg:gap-8 mb-6 sm:mb-8 lg:mb-10 ${createScrollAnimationClasses('fadeInUp', statsVisible)}`}>
              <div className="group text-center lg:text-left bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 border border-blue-200/50 shadow-lg sm:shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 sm:hover:-translate-y-2">
                <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto lg:mx-0 mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Zap size={20} className="sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-blue-600 mb-1 sm:mb-2 group-hover:text-blue-700 transition-colors">70</div>
                <div className="text-xs sm:text-sm lg:text-base text-gray-700 font-semibold">Energy Savings</div>
              </div>
              <div className="group text-center lg:text-left bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 border border-green-200/50 shadow-lg sm:shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 sm:hover:-translate-y-2">
                <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-green-500 to-teal-600 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto lg:mx-0 mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Shield size={20} className="sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-green-600 mb-1 sm:mb-2 group-hover:text-green-700 transition-colors">500+</div>
                <div className="text-xs sm:text-sm lg:text-base text-gray-700 font-semibold">Projects Delivered</div>
              </div>
              <div className="group text-center lg:text-left bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 border border-purple-200/50 shadow-lg sm:shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 sm:hover:-translate-y-2">
                <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto lg:mx-0 mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-300">
                  <TrendingUp size={20} className="sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-purple-600 mb-1 sm:mb-2 group-hover:text-purple-700 transition-colors">$1M</div>
                <div className="text-xs sm:text-sm lg:text-base text-gray-700 font-semibold">Annual R&D Investment</div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div ref={buttonsRef} className={`flex flex-col sm:flex-row gap-3 sm:gap-4 lg:gap-6 justify-center lg:justify-start mb-6 sm:mb-8 lg:mb-12 ${createScrollAnimationClasses('fadeInUp', buttonsVisible)}`}>
              <Button 
                size="lg"
                onClick={() => scrollToSection('services')}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-4 sm:px-6 lg:px-8 xl:px-10 py-3 sm:py-4 lg:py-5 rounded-lg sm:rounded-xl text-sm sm:text-base lg:text-lg xl:text-xl font-semibold transition-all duration-300 hover:scale-105 shadow-lg sm:shadow-xl hover:shadow-2xl w-full sm:w-auto"
              >
                <span className="hidden sm:inline">Discover Our Solutions</span>
                <span className="sm:hidden">Discover Solutions</span>
                <ArrowRight size={16} className="sm:w-5 sm:h-5 ml-1.5 sm:ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => scrollToSection('video')}
                className="border-2 border-gray-300 hover:border-blue-600 text-gray-700 hover:text-blue-600 px-4 sm:px-6 lg:px-8 xl:px-10 py-3 sm:py-4 lg:py-5 rounded-lg sm:rounded-xl text-sm sm:text-base lg:text-lg xl:text-xl font-semibold transition-all duration-300 hover:scale-105 hover:bg-blue-50 w-full sm:w-auto"
              >
                <Play size={16} className="sm:w-5 sm:h-5 mr-1.5 sm:mr-2 group-hover:scale-110 transition-transform" />
                Watch Demo
              </Button>
            </div>
          </div>

          {/* Hero Video/Image */}
          <div ref={imageRef} className={`relative flex items-center justify-center ${createScrollAnimationClasses('fadeInRight', imageVisible)}`}>
            <div className="relative z-10 w-full max-w-xs sm:max-w-2xl lg:max-w-3xl mx-auto px-2 sm:px-0">
              {/* Enhanced Video Container */}
              <div className="group relative">
                {/* Video Frame with Enhanced Styling */}
                <div className="relative overflow-hidden rounded-xl sm:rounded-2xl lg:rounded-3xl shadow-lg sm:shadow-xl lg:shadow-2xl bg-gradient-to-br from-gray-900 via-black to-gray-800 hover:shadow-3xl transition-all duration-700 transform hover:scale-[1.01] sm:hover:scale-[1.02] border border-white/20 sm:border-2 lg:border-4">
                  {/* Decorative Corner Elements */}
                  <div className="absolute top-2 sm:top-4 left-2 sm:left-4 w-4 sm:w-6 lg:w-8 h-4 sm:h-6 lg:h-8 border-l-2 border-t-2 border-blue-400 rounded-tl-lg opacity-60"></div>
                  <div className="absolute top-2 sm:top-4 right-2 sm:right-4 w-4 sm:w-6 lg:w-8 h-4 sm:h-6 lg:h-8 border-r-2 border-t-2 border-purple-400 rounded-tr-lg opacity-60"></div>
                  <div className="absolute bottom-2 sm:bottom-4 left-2 sm:left-4 w-4 sm:w-6 lg:w-8 h-4 sm:h-6 lg:h-8 border-l-2 border-b-2 border-green-400 rounded-bl-lg opacity-60"></div>
                  <div className="absolute bottom-2 sm:bottom-4 right-2 sm:right-4 w-4 sm:w-6 lg:w-8 h-4 sm:h-6 lg:h-8 border-r-2 border-b-2 border-orange-400 rounded-br-lg opacity-60"></div>
                  
                  {/* Video Content */}
                  <div className="relative aspect-video w-full min-h-[200px] sm:min-h-[280px] lg:min-h-[320px] xl:min-h-[400px]">
                    {USE_HERO_VIDEO && !videoError ? (
                      <video 
                        ref={videoRef}
                        className="w-full h-full object-cover"
                        poster={mockData.hero.fallbackImage}
                        preload="metadata"
                        loop
                        autoPlay
                        muted
                        playsInline
                        controls
                        controlsList="nodownload noremoteplayback"
                        onError={(e) => {
                          console.error('Video failed to load:', e);
                          setVideoError(true);
                          setVideoLoading(false);
                        }}
                        onLoadStart={() => {
                          console.log('Video loading started');
                          setVideoLoading(true);
                        }}
                        onCanPlay={() => {
                          console.log('Video can play');
                          setVideoLoading(false);
                        }}
                        onLoadedData={() => {
                          console.log('Video data loaded');
                          setVideoLoading(false);
                        }}
                        onPlay={() => {
                          console.log('Video started playing');
                          setIsVideoPlaying(true);
                        }}
                        onPause={() => {
                          console.log('Video paused');
                          setIsVideoPlaying(false);
                        }}
                        onEnded={() => {
                          console.log('Video ended');
                          setIsVideoPlaying(false);
                        }}
                      >
                        {/* Video paths — restore when USE_HERO_VIDEO is true */}
                        {/* <source src="/Website_Work/Smart_city_connect_video/WhatsApp_Video_2025-09-02_at_2.36.46_PM.mp4" type="video/mp4" /> */}
                        {/* <source src="/Website_Work/01_Smart_Lighting/Schréder_VisioLum.mp4.mp4" type="video/mp4" /> */}
                      </video>
                    ) : (
                      <img 
                        src={mockData.hero.fallbackImage}
                        alt="Smart City Technology"
                        className="w-full h-full object-cover"
                      />
                    )}
                    
                    {/* Enhanced Loading indicator */}
                    {USE_HERO_VIDEO && videoLoading && !videoError && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-black/80 via-gray-900/80 to-black/80 backdrop-blur-sm">
                        <div className="text-center px-4">
                          <div className="relative mb-4 sm:mb-6">
                            <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto animate-pulse">
                              <Play size={18} className="sm:w-6 sm:h-6 text-white" />
                            </div>
                            <div className="absolute inset-0 w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 border-2 border-blue-400 rounded-xl sm:rounded-2xl animate-spin mx-auto"></div>
                          </div>
                          <h3 className="text-lg sm:text-xl font-bold text-white mb-1 sm:mb-2">Loading Experience</h3>
                          <p className="text-sm sm:text-base text-gray-300">Preparing your smart city journey...</p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Video Overlay Effects - Hidden when playing */}
                  {USE_HERO_VIDEO && !isVideoPlaying && (
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none"></div>
                  )}
                </div>
                
                {/* Enhanced Background Effects */}
                <div className="absolute -inset-1 sm:-inset-2 lg:-inset-4 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-xl sm:rounded-2xl lg:rounded-3xl blur-lg sm:blur-xl lg:blur-2xl opacity-60 group-hover:opacity-80 transition-opacity duration-500 -z-10"></div>
                <div className="absolute -inset-2 sm:-inset-4 lg:-inset-8 bg-gradient-to-r from-cyan-400/10 via-blue-400/10 to-indigo-400/10 rounded-xl sm:rounded-2xl lg:rounded-3xl blur-xl sm:blur-2xl lg:blur-3xl opacity-40 group-hover:opacity-60 transition-opacity duration-700 -z-20"></div>
              </div>
              
              {/* Additional Content Below Video */}
              <div className="mt-3 sm:mt-4 lg:mt-6 text-center lg:text-left px-1 sm:px-0">
                <div className="inline-flex items-center gap-1.5 sm:gap-2 lg:gap-3 bg-gradient-to-r from-blue-50 to-purple-50 px-2 sm:px-3 lg:px-4 xl:px-6 py-1.5 sm:py-2 lg:py-3 rounded-full border border-blue-200/50">
                  <div className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 xl:w-8 xl:h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                    <Play size={8} className="sm:w-2.5 sm:h-2.5 lg:w-3 lg:h-3 xl:w-4 xl:h-4 text-white" />
                  </div>
                  <span className="text-xs sm:text-sm lg:text-base text-blue-700 font-semibold">Experience Smart City Innovation</span>
                </div>
                
                {/* Quick Stats Row */}
                <div className="mt-2 sm:mt-3 lg:mt-4 xl:mt-6 grid grid-cols-3 gap-0.5 sm:gap-1 lg:gap-2 xl:gap-4">
                  <div className="text-center">
                    <div className="text-xs sm:text-sm lg:text-base xl:text-lg 2xl:text-xl font-bold text-blue-600">24/7</div>
                    <div className="text-xs sm:text-sm text-gray-600">Monitoring</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs sm:text-sm lg:text-base xl:text-lg 2xl:text-xl font-bold text-green-600">99.9%</div>
                    <div className="text-xs sm:text-sm text-gray-600">Uptime</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs sm:text-sm lg:text-base xl:text-lg 2xl:text-xl font-bold text-purple-600">AI</div>
                    <div className="text-xs sm:text-sm text-gray-600">Powered</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-2 sm:bottom-4 lg:bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce no-print">
        <div className="w-4 sm:w-5 lg:w-6 h-6 sm:h-8 lg:h-10 border-2 border-gray-300 rounded-full flex justify-center hover:border-blue-500 transition-colors">
          <div className="w-1 h-1.5 sm:h-2 lg:h-3 bg-gray-300 rounded-full mt-1 sm:mt-1.5 lg:mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};
